import type { CSSProperties } from "react";

import type { StageBranding, StageSnapshot, StageTheme } from "@msc/shared";

/**
 * Kundendesign auf der Bühne (D10): aus Primär- und Akzentfarbe des Kunden die
 * Custom Properties der Bühne (`stage.css`) ableiten.
 *
 * Die Kontrastregel aus `stage.css` gilt unverändert weiter — nur rechnet sie
 * hier, weil die Farben erst zur Laufzeit feststehen: Der Vordergrund ist Weiß
 * oder Navy, je nachdem, was gegen die Primärfarbe den höheren Kontrast hat
 * (WCAG 2.x, relative Luminanz). Liegt einer der beiden bei ≥ 4,5:1, gewinnt
 * damit automatisch einer, der AA für Fließtext hält; liegt keiner darüber
 * (Mitteltöne um L ≈ 0,18), bleibt es wenigstens der bessere.
 *
 * Orange-Regel (s. `stage.css`): Der Akzent trägt nie Fließtext, nur Flächen
 * (Fortschrittsbalken, Rahmen, Badge-Hintergrund mit eigens berechneter
 * Textfarbe). Die Akzentfarbe des Kunden ist ungeprüft — sie darf deshalb nur
 * dorthin, wo auch Orange hindarf.
 */

/** CI-Navy — dieselbe Farbe wie `--stage-bg` im dunklen Theme. */
export const STAGE_NAVY = "#1B365D";
export const STAGE_WHITE = "#ffffff";

/** mindsquare-Farben als Vorschläge im Farbfeld der Kundenakte. */
export const MINDSQUARE_SWATCHES = ["#1b365d", "#fdc300", "#ea5b0b", "#565656"] as const;

const HEX = /^#([0-9a-f]{6})$/i;

function rgb(hex: string): [number, number, number] {
  const m = HEX.exec(hex);
  if (!m) return [0, 0, 0];
  const n = Number.parseInt(m[1] as string, 16);
  return [(n >> 16) & 0xff, (n >> 8) & 0xff, n & 0xff];
}

function toHex([r, g, b]: [number, number, number]): string {
  return `#${[r, g, b].map((c) => Math.round(c).toString(16).padStart(2, "0")).join("")}`;
}

/** Relative Luminanz nach WCAG 2.x (sRGB linearisiert). */
export function luminanz(hex: string): number {
  const [r, g, b] = rgb(hex).map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  }) as [number, number, number];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** Kontrastverhältnis zweier Farben, 1 … 21. */
export function kontrast(a: string, b: string): number {
  const la = luminanz(a);
  const lb = luminanz(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/**
 * Vordergrund für eine Fläche: Weiß oder Navy, was den höheren Kontrast hat.
 * Gleichstand geht an Weiß.
 */
export function kontrastFarbe(hex: string): typeof STAGE_WHITE | typeof STAGE_NAVY {
  return kontrast(hex, STAGE_WHITE) >= kontrast(hex, STAGE_NAVY) ? STAGE_WHITE : STAGE_NAVY;
}

/** Jede Farbkomponente um `prozent` Richtung Schwarz. */
export function abdunkeln(hex: string, prozent: number): string {
  const f = 1 - Math.min(Math.max(prozent, 0), 100) / 100;
  const [r, g, b] = rgb(hex);
  return toHex([r * f, g * f, b * f]);
}

function rgba(hex: string, alpha: number): string {
  const [r, g, b] = rgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Trägt das Branding überhaupt ein Design? Ein Logo allein ist keines — es
 * steht in jedem Theme in der Kopfzeile; die Stufe `customer` braucht Farben.
 */
export function hasCustomerDesign(branding: StageBranding | null | undefined): branding is StageBranding {
  return branding != null && (branding.primaryColor !== null || branding.accentColor !== null);
}

/**
 * Die Custom Properties der Stufe `customer`. Ohne Primärfarbe bleibt der
 * Grund Navy (nur der Akzent ist dann Kundenfarbe), ohne Akzent übernimmt die
 * abgedunkelte Primärfarbe dessen Flächen.
 *
 * Badge ohne Akzent: Vorder- und Hintergrund der Folie vertauscht statt
 * Primär auf Primär — die Badge-Fläche bliebe sonst unsichtbar, und das
 * Farbpaar ist per Konstruktion dasselbe, dessen Kontrast oben gewählt wurde.
 */
export function stageVars(branding: StageBranding): Record<string, string> {
  const primary = branding.primaryColor ?? STAGE_NAVY.toLowerCase();
  const fg = kontrastFarbe(primary);
  const accent = branding.accentColor ?? abdunkeln(primary, 30);
  const badgeBg = branding.accentColor ?? fg;
  return {
    "--stage-bg": primary,
    "--stage-bg-2": abdunkeln(primary, 12),
    "--stage-fg": fg,
    "--stage-muted": rgba(fg, 0.8),
    "--stage-card": rgba(fg, 0.08),
    "--stage-card-border": rgba(fg, 0.14),
    "--stage-accent": accent,
    "--stage-accent-2": accent,
    "--stage-badge-bg": badgeBg,
    "--stage-badge-fg": branding.accentColor ? kontrastFarbe(badgeBg) : primary,
    "--stage-link": fg,
  };
}

/**
 * Welches Theme die Bühne tatsächlich zeichnet, und mit welchen Variablen.
 *
 * `customer` greift nur, wenn der Snapshot ein Design mitbringt — ein älteres
 * Cockpit ohne `branding`, oder ein Kunde ohne Farben, landet auf `dark`
 * (Spec D5: nie brechen). Dieselbe Entscheidung für Bühne, Cockpit-Vorschau
 * und die Vorschau in der Kundenakte, damit die drei nie auseinanderlaufen.
 */
export function resolveStageTheme(
  snapshot: Pick<StageSnapshot, "theme" | "branding"> | null,
): { theme: StageTheme; style: CSSProperties | undefined } {
  const theme = snapshot?.theme ?? "dark";
  if (theme === "customer") {
    const branding = snapshot?.branding ?? null;
    return hasCustomerDesign(branding)
      ? { theme: "customer", style: stageVars(branding) as CSSProperties }
      : { theme: "dark", style: undefined };
  }
  return { theme: theme === "light" ? "light" : "dark", style: undefined };
}
